import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { tenantApi } from '../../api/endpoints';
import type { CompanyDetailsState } from '../../api/types';
import { useI18n } from '../../i18n';
import { Dialog, Icon, useToast } from '../../ui';
import { countryOptions } from '../crm/address/format';
import { CompanyDetailsForm } from './CompanyDetailsForm';
import { useCurrentCompany } from './hooks';

// A company's details where they are used (TF-79): on Plan and billing (they are what invoices say),
// and - for a company registered with a name only - a banner asking its owners to complete them.

export function useDetails(tenantId: string, enabled = true) {
  return useQuery({ queryKey: ['company-details', tenantId], queryFn: () => tenantApi.companyDetails(tenantId), enabled });
}

export function EditDetailsDialog({ tenantId, details, open, onOpenChange }: { tenantId: string; details: CompanyDetailsState; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('company.detailsTitle')} wide>
      <CompanyDetailsForm
        mode="edit"
        tenantId={tenantId}
        initial={details}
        onCancel={() => onOpenChange(false)}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company-details', tenantId] });
          await queryClient.invalidateQueries({ queryKey: ['companies'] });
          await queryClient.invalidateQueries({ queryKey: ['billing-details', tenantId] });
          toast.info(t('company.detailsSaved'));
          onOpenChange(false);
        }}
      />
    </Dialog>
  );
}

/** The details as a list, with Edit for the company's owners. */
export function CompanyDetailsSummary({ tenantId, hint }: { tenantId: string; hint?: string }) {
  const { t, language } = useI18n();
  const { company } = useCurrentCompany();
  const isOwner = company?.role === 'owner';
  const details = useDetails(tenantId);
  const [editing, setEditing] = useState(false);
  const countries = useMemo(() => new Map(countryOptions(language).map((country) => [country.code, country.name])), [language]);
  const data = details.data?.details;
  if (!data) return null;
  const given = (value: string | null | undefined) => value || <span className="muted">{t('company.notGiven')}</span>;
  const address = data.legalAddress ? [data.legalAddress.line1, [data.legalAddress.postalCode, data.legalAddress.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') : null;
  return (
    <section className="card" aria-labelledby="company-details-heading">
      <div className="card-head">
        <h2 id="company-details-heading">{t('company.detailsTitle')}</h2>
        {isOwner && (
          <button type="button" className="btn btn-sm" onClick={() => setEditing(true)}>
            <Icon name="edit" size={18} />
            {t('crm.edit')}
          </button>
        )}
      </div>
      {hint && <p className="hint">{hint}</p>}
      <dl className="kv">
        <dt>{t('company.legalName')}</dt>
        <dd>{given(data.legalName)}</dd>
        <dt>{t('company.country')}</dt>
        <dd>{given(data.country && (countries.get(data.country) ?? data.country))}</dd>
        <dt>{t('company.registrationNo')}</dt>
        <dd>{given(data.registrationNo)}</dd>
        <dt>{t('company.vatNo')}</dt>
        <dd>
          {given(data.vatNo)} {data.numberStatus === 'checked' && <span className="badge badge-success">{t('company.numberChecked')}</span>}
        </dd>
        <dt>{t('company.address')}</dt>
        <dd>{given(address)}</dd>
        <dt>{t('company.invoiceEmail')}</dt>
        <dd>{given(data.invoiceEmail)}</dd>
      </dl>
      {editing && <EditDetailsDialog tenantId={tenantId} details={data} open={editing} onOpenChange={setEditing} />}
    </section>
  );
}

const DISMISS_KEY = (tenantId: string) => `teamfolio.completeDetailsLater.${tenantId}`;
const DISMISS_MS = 30 * 24 * 3600_000;

/**
 * For the owners of a company registered with a name only (TF-79): complete its details. Not now
 * hides it for 30 days in this browser; nothing is blocked.
 */
export function CompleteDetailsBanner() {
  const { t } = useI18n();
  const { tenantId, company } = useCurrentCompany();
  const [dismissedAt, setDismissedAt] = useState<number>(() => {
    try {
      return Number(localStorage.getItem(DISMISS_KEY(tenantId ?? '')) ?? 0);
    } catch {
      return 0;
    }
  });
  const [editing, setEditing] = useState(false);
  const show = Boolean(tenantId && company?.role === 'owner' && company.numberStatus === 'none' && Date.now() - dismissedAt > DISMISS_MS);
  const details = useDetails(tenantId ?? '', show || editing);
  if (!tenantId || (!show && !editing)) return null;
  const later = () => {
    const now = Date.now();
    setDismissedAt(now);
    try {
      localStorage.setItem(DISMISS_KEY(tenantId), String(now));
    } catch {
      // Hidden until the page is left.
    }
  };
  return (
    <>
      {show && (
        <section className="banner invitation-banner complete-details" aria-label={t('company.completeTitle')}>
          <Icon name="apartment" className="icon-lead" />
          <p className="grow m-0">{t('company.completeText')}</p>
          <div className="btn-row">
            <button type="button" className="btn btn-sm btn-primary" disabled={!details.data} onClick={() => setEditing(true)}>
              {t('company.complete')}
            </button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={later}>
              {t('company.notNow')}
            </button>
          </div>
        </section>
      )}
      {editing && details.data && <EditDetailsDialog tenantId={tenantId} details={details.data.details} open={editing} onOpenChange={setEditing} />}
    </>
  );
}
